/* Original size comparison. Dimensions are exterior references, never packing claims. */
(() => {
  'use strict';
  const POUCH = Object.freeze({ width: 30, height: 20, depth: 0.6 });
  const OBJECTS = Object.freeze({
    phone: { width: 7.67, height: 15.99, depth: 0.825, art: [75, 150], name: 'iPhone 15 Pro Max' },
    tablet: { width: 24.76, height: 17.85, depth: 0.59, art: [248, 179], name: 'iPad Pro 11″ (2022)' },
    laptop: { width: 30.41, height: 21.24, depth: 1.56, art: [304, 215], name: 'MacBook Pro 13″ (M2, 2022)' },
    wallet: { width: 19, height: 10, depth: 2, art: [190, 100], name: 'Zip wallet (example)', fr: 'Portefeuille zippé (exemple)' }
  });
  const format = (value, unit, language) => {
    const converted = unit === 'in' ? value / 2.54 : value;
    return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-GB', { maximumFractionDigits: unit === 'in' ? 2 : 3 }).format(converted) + ' ' + unit;
  };
  const compareGeometry = (key) => {
    const object = OBJECTS[key] || OBJECTS.phone;
    const scale = 10; // Both objects use exactly ten SVG units per centimetre.
    return { pouch: { x: 55, y: 350 - POUCH.height * scale, width: POUCH.width * scale, height: POUCH.height * scale }, object: { x: 425, y: 350 - object.height * scale, width: object.width * scale, height: object.height * scale } };
  };
  const bodyGeometry = (height) => {
    const safeHeight = Math.min(200, Math.max(145, Number(height) || 170));
    const scale = 380 / safeHeight;
    return { height: safeHeight, person: { x: 327, y: 32, width: 60 / 170 * 380, height: 380 }, pouch: { x: 327 - POUCH.width * scale * 0.9, y: 32 + 380 * 0.55, width: POUCH.width * scale, height: POUCH.height * scale } };
  };
  // Pure geometry is exported only in local Node tests; no dependencies at runtime.
  if (typeof module !== 'undefined' && module.exports) { module.exports = { POUCH, OBJECTS, format, compareGeometry, bodyGeometry }; return; }
  if (window.CrocoSizeGuide) { window.CrocoSizeGuide.init(document); return; }
  const NS = 'http://www.w3.org/2000/svg';
  const svgNode = (tag, attributes, content) => {
    const node = document.createElementNS(NS, tag);
    Object.entries(attributes || {}).forEach(([name, value]) => node.setAttribute(name, String(value)));
    if (content !== undefined) node.textContent = content;
    return node;
  };
  const initialize = (root) => {
    if (root.dataset.csgReady) return;
    const dialog = root.querySelector('[data-csg-dialog]');
    const opener = root.querySelector('[data-csg-open]');
    if (!dialog || !opener) return;
    if (typeof dialog.showModal !== 'function') {
      opener.hidden = false;
      opener.textContent = '30 × 20 × 0.6 cm';
      opener.disabled = true;
      return;
    }
    const language = root.dataset.lang;
    const fr = language === 'fr';
    const f = (value) => format(value, unit, language);
    const prefix = root.dataset.artPrefix;
    const scene = root.querySelector('[data-csg-scene]');
    const canvas = root.querySelector('[data-csg-canvas]');
    const context = root.querySelector('[data-csg-context]');
    const tabs = [...root.querySelectorAll('[data-csg-tab]')];
    let mode = 'compare', selection = 'phone', unit = 'cm', zoom = 1, height = 170;
    let previousFocus, previousOverflow, previousPadding;
    const addArt = (target, name, box, artSize) => {
      const group = svgNode('g', { transform: `translate(${box.x} ${box.y}) scale(${box.width / artSize[0]} ${box.height / artSize[1]})` });
      group.append(svgNode('use', { href: `#${prefix}sg-${name}`, width: artSize[0], height: artSize[1] }));
      target.append(group);
    };
    const label = (group, x, y, text, small = false) => group.append(svgNode('text', { x, y, 'text-anchor': 'middle', ...(small ? { class: 'csg-svg-small' } : {}) }, text));
    const dimensions = (object) => [object.width, object.height, object.depth].map(f).join(' × ');
    const render = () => {
      scene.replaceChildren();
      const group = svgNode('g', { transform: `translate(380 230) scale(${zoom}) translate(-380 -230)` });
      scene.append(group);
      if (mode === 'compare') {
        const object = OBJECTS[selection];
        const boxes = compareGeometry(selection);
        const name = fr && object.fr ? object.fr : object.name;
        addArt(group, 'pouch', boxes.pouch, [300, 200]);
        addArt(group, selection, boxes.object, object.art);
        group.append(svgNode('path', { d: 'M35 354H742', class: 'csg-dimension' }));
        label(group, 205, 385, 'Croco Studio');
        label(group, 205, 407, `${f(30)} × ${f(20)}`, true);
        label(group, boxes.object.x + boxes.object.width / 2, 385, fr ? ({phone:'Téléphone',tablet:'Tablette',laptop:'Ordinateur',wallet:'Portefeuille'})[selection] : ({phone:'Phone',tablet:'Tablet',laptop:'Laptop',wallet:'Wallet'})[selection]);
        label(group, boxes.object.x + boxes.object.width / 2, 407, `${f(object.width)} × ${f(object.height)}`, true);
        const warning = selection === 'laptop'
          ? (fr ? 'Plus large et plus haut que la pochette : ne convient pas.' : 'Wider and taller than the pouch: does not fit.')
          : (fr ? 'Comparaison extérieure uniquement : contenance non vérifiée.' : 'Exterior comparison only: internal fit is not verified.');
        context.textContent = `${name} · ${dimensions(object)} (${fr ? 'largeur × hauteur × épaisseur' : 'width × height × thickness'}). ${warning}`;
        canvas.setAttribute('aria-label', `${fr ? 'À la même échelle' : 'At the same scale'}: Croco Studio 30 × 20 cm; ${name} ${object.width} × ${object.height} cm.`);
      } else if (mode === 'dimensions') {
        addArt(group, 'pouch', { x: 110, y: 65, width: 390, height: 260 }, [300, 200]);
        // Side depth uses the same 13 px/cm scale as the front view (7.8 px).
        group.append(svgNode('rect', { x: 598, y: 65, width: 7.8, height: 260, rx: 1, fill: '#243c49' }));
        group.append(svgNode('path', { d:'M110 342V365M500 342V365M110 355H500M110 355l8-4m-8 4l8 4M500 355l-8-4m8 4l-8 4M85 65H99M85 325H99M91 65V325M91 65l-4 8m4-8l4 8M91 325l-4-8m4 8l4-8M598 337V360M606 337V360M576 352H628', class:'csg-dimension' }));
        label(group, 305, 387, f(30));
        group.append(svgNode('text', { transform:'translate(62 195) rotate(-90)', 'text-anchor':'middle' }, f(20)));
        label(group, 602, 387, f(0.6));
        label(group, 305, 409, fr ? 'Vue de face' : 'Front view', true);
        label(group, 602, 409, fr ? 'Profil' : 'Side view', true);
        context.textContent = fr ? 'Dimensions issues des spécifications de la Croco Embossed Pouch.' : 'Dimensions taken from the Croco Embossed Pouch specifications.';
        canvas.setAttribute('aria-label', `${fr ? 'Longueur, hauteur, profondeur' : 'Length, height, depth'}: ${f(30)}, ${f(20)}, ${f(0.6)}.`);
      } else {
        const boxes = bodyGeometry(height);
        addArt(group, 'person', boxes.person, [60, 170]);
        addArt(group, 'pouch', boxes.pouch, [300, 200]);
        group.append(svgNode('path', { d:'M514 32H530M514 412H530M522 32V412', class:'csg-dimension' }));
        group.append(svgNode('text', { transform:'translate(550 222) rotate(-90)', 'text-anchor':'middle' }, f(height)));
        label(group, 382, 440, fr ? 'Pochette tenue à la main · illustration' : 'Pouch held by hand · illustration', true);
        context.textContent = `${fr ? 'Pochette' : 'Pouch'} ${f(30)} × ${f(20)} · ${fr ? 'silhouette de' : 'silhouette height'} ${f(height)}.`;
        canvas.setAttribute('aria-label', context.textContent);
      }
      root.querySelectorAll('[data-csg-measure]').forEach((node) => { node.textContent = f(Number(node.dataset.csgMeasure)); });
      root.querySelector('[data-csg-height-label]').textContent = f(height);
      root.querySelector('[data-csg-height]').setAttribute('aria-valuetext', f(height));
      root.querySelector('[data-csg-reset]').textContent = `${Math.round(zoom * 100)}%`;
      root.querySelector('[data-csg-zoom="out"]').disabled = zoom <= 0.75;
      root.querySelector('[data-csg-zoom="in"]').disabled = zoom >= 1.75;
    };
    const open = () => {
      if (dialog.open) return;
      previousFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;
      previousPadding = document.body.style.paddingRight;
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      if (scrollbar) document.body.style.paddingRight = `${parseFloat(getComputedStyle(document.body).paddingRight) + scrollbar}px`;
      render();
      root.querySelector('[data-csg-close]').focus();
    };
    const restore = () => {
      document.body.style.overflow = previousOverflow ?? '';
      document.body.style.paddingRight = previousPadding ?? '';
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
    opener.addEventListener('click', open);
    root.querySelector('[data-csg-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', restore);
    // Native Escape and focus trapping are provided by HTMLDialogElement.
    let backdropDown = false;
    const outside = (event) => { const rect = dialog.getBoundingClientRect(); return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom; };
    dialog.addEventListener('pointerdown', (event) => { backdropDown = event.target === dialog && outside(event); });
    dialog.addEventListener('click', (event) => { if (backdropDown && event.target === dialog && outside(event)) dialog.close(); backdropDown = false; });
    const setTab = (button) => {
      mode = button.dataset.csgTab; zoom = 1;
      tabs.forEach((tab) => { const selected = tab === button; tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1; });
      root.querySelectorAll('[data-csg-panel]').forEach((panel) => { panel.hidden = panel.dataset.csgPanel !== mode; });
      render();
    };
    tabs.forEach((button, index) => {
      button.addEventListener('click', () => setTab(button));
      button.addEventListener('keydown', (event) => {
        const moves = { ArrowRight: (index + 1) % tabs.length, ArrowLeft: (index + tabs.length - 1) % tabs.length, Home: 0, End: tabs.length - 1 };
        if (!(event.key in moves)) return;
        event.preventDefault(); const next = tabs[moves[event.key]]; setTab(next); next.focus();
      });
    });
    root.querySelectorAll('[data-csg-object]').forEach((button) => button.addEventListener('click', () => {
      selection = button.dataset.csgObject; zoom = 1;
      root.querySelectorAll('[data-csg-object]').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
      render();
    }));
    root.querySelectorAll('[data-csg-unit]').forEach((button) => button.addEventListener('click', () => {
      unit = button.dataset.csgUnit;
      root.querySelectorAll('[data-csg-unit]').forEach((other) => other.setAttribute('aria-pressed', String(other === button))); render();
    }));
    root.querySelector('[data-csg-height]').addEventListener('input', (event) => { height = bodyGeometry(event.target.value).height; render(); });
    root.querySelectorAll('[data-csg-zoom]').forEach((button) => button.addEventListener('click', () => { zoom = Math.min(1.75, Math.max(0.75, zoom + (button.dataset.csgZoom === 'in' ? 0.25 : -0.25))); render(); }));
    root.querySelector('[data-csg-reset]').addEventListener('click', () => { zoom = 1; render(); });
    root.csgCleanup = () => { if (dialog.open) { dialog.close(); restore(); } };
    root.dataset.csgReady = 'true'; opener.hidden = false; render();
  };
  const init = (scope) => { if (scope.matches?.('[data-csg]')) initialize(scope); scope.querySelectorAll('[data-csg]').forEach(initialize); };
  window.CrocoSizeGuide = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-csg]').forEach((root) => root.csgCleanup?.()));
})();
